import { Button } from "@frontend/components/ui/button"
import { Card, CardContent } from "@frontend/components/ui/card"
import { DataTable } from "@frontend/components/ui/data-table"
import { Input } from "@frontend/components/ui/input"
import { Label } from "@frontend/components/ui/label"
import { Progress } from "@frontend/components/ui/progress"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@frontend/components/ui/select"
import { Switch } from "@frontend/components/ui/switch"
import { useApiQuery } from "@frontend/hooks/use-api"
import { useInstanceTags } from "@frontend/hooks/use-instance"
import { type ProjectFilters, useProjects } from "@frontend/hooks/use-projects"
import type { ColumnDef } from "@tanstack/react-table"
import { FolderOpen, Plus, Search } from "lucide-react"
import { useMemo, useState } from "react"
import { useNavigate } from "react-router"

interface Project {
  id: string
  name: string
  description: string | null
  mode: string
  status: string
  review_enabled: number
  current_user_role: "instance_admin" | "project_admin" | "annotator" | "reviewer" | "viewer" | null
  readers_per_case: number
  created_at: string
  progress_summary?: {
    completed_cases: number
    total_cases: number
    completion_percentage: number
  }
}

interface Tag {
  id: string
  name: string
  color: string
}

function formatCurrentUserRole(role: Project["current_user_role"]) {
  if (role === "instance_admin") return "Instance Admin"
  if (role === "project_admin") return "Project Admin"
  if (role === "annotator") return "Annotator"
  if (role === "reviewer") return "Reviewer"
  if (role === "viewer") return "Viewer"
  return "-"
}

export interface ProjectListViewProps {
  search: string
  statusFilter: string
  showArchived: boolean
  selectedTagIds: string[]
  tags: Tag[]
  projects: Project[]
  totalProjects: number
  isLoading: boolean
  onSearchChange: (value: string) => void
  onStatusFilterChange: (value: string) => void
  onShowArchivedChange: (value: boolean) => void
  onToggleTag: (tagId: string) => void
  onNewProject: () => void
  onOpenProject: (project: Project) => void
  columns: ColumnDef<Project>[]
  isInstanceAdmin?: boolean
}

export function ProjectListView({
  search,
  statusFilter,
  showArchived,
  selectedTagIds,
  tags,
  projects,
  totalProjects,
  isLoading,
  onSearchChange,
  onStatusFilterChange,
  onShowArchivedChange,
  onToggleTag,
  onNewProject,
  onOpenProject,
  columns,
  isInstanceAdmin = false
}: ProjectListViewProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1>Projects</h1>
        {isInstanceAdmin && (
          <Button size="sm" onClick={onNewProject}>
            <Plus className="size-3.5 mr-1.5" />
            New Project
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3 mb-1">
        <div className="relative flex-1 max-w-[320px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-8 h-8"
          />
        </div>
        <Select value={statusFilter} onValueChange={(value) => onStatusFilterChange(value ?? "all")}>
          <SelectTrigger className="w-36 h-8">
            <SelectValue placeholder="Status">
              {{ all: "All", active: "Active", archived: "Archived" }[statusFilter]}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 ml-auto">
          <Switch id="show-archived" checked={showArchived} onCheckedChange={onShowArchivedChange} />
          <Label htmlFor="show-archived" className="text-[12px] text-muted-foreground cursor-pointer">
            Include archived
          </Label>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        {tags.length > 0 ? (
          <div className="flex items-center gap-1.5 flex-wrap">
            {tags.map((tag) => (
              <button
                key={tag.id}
                onClick={() => onToggleTag(tag.id)}
                className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs border transition-colors ${
                  selectedTagIds.includes(tag.id) ? "border-primary bg-primary/10" : "border-border hover:bg-accent"
                }`}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />
                {tag.name}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading projects...</p>
      ) : projects.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <FolderOpen className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No projects found.{" "}
              {statusFilter !== "all" || selectedTagIds.length > 0
                ? "Try adjusting your filters."
                : "Create your first project to get started."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="bg-card rounded-lg border overflow-auto">
          <DataTable
            columns={columns}
            data={projects}
            emptyMessage="No projects found."
            showPagination
            onRowClick={onOpenProject}
          />
        </div>
      )}

      {totalProjects > 0 ? (
        <p className="text-xs text-muted-foreground">
          Showing {projects.length} of {totalProjects} projects
        </p>
      ) : null}
    </div>
  )
}

export function ProjectListContainer() {
  const navigate = useNavigate()
  const { data: currentUser } = useApiQuery<{
    instance_role: "admin" | "member" | null
  }>(["auth", "me"], "/auth/me")
  const isInstanceAdmin = currentUser?.instance_role === "admin"
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [showArchived, setShowArchived] = useState(false)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])

  const filters: ProjectFilters = {}
  if (statusFilter !== "all") {
    filters.status = statusFilter as "active" | "archived"
  }
  if (selectedTagIds.length > 0) {
    filters.tag_ids = selectedTagIds.join(",")
  }

  // The API caps pages at 100; search/archived filtering below is client side, so fetch one large page.
  const { data: projectsResult, isLoading } = useProjects({ ...filters, limit: 100 })
  const { data: tagsResult } = useInstanceTags()

  const projects: Project[] = (projectsResult?.data ?? []).filter((p: Project) => {
    if (!showArchived && p.status === "archived") return false
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })
  const tags: Tag[] = tagsResult?.data ?? []

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) => (prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]))
  }

  const columns = useMemo<ColumnDef<Project>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => {
          const project = row.original
          return (
            <div>
              <p className="font-medium text-[13px]">{project.name}</p>
              {project.description ? (
                <p className="text-[11px] text-muted-foreground mt-0.5 max-w-[380px] truncate">{project.description}</p>
              ) : null}
            </div>
          )
        }
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-foreground capitalize">
            {row.original.status.replace("_", " ")}
          </span>
        )
      },
      {
        accessorKey: "mode",
        header: "Mode",
        cell: ({ row }) => (
          <span className="text-[11px] px-2 py-0.5 rounded bg-muted/60 capitalize">{row.original.mode}</span>
        )
      },
      {
        id: "progress",
        accessorFn: (row) => row.progress_summary?.completion_percentage ?? 0,
        header: "Progress",
        cell: ({ row }) => {
          const completedCases = row.original.progress_summary?.completed_cases ?? 0
          const totalCases = row.original.progress_summary?.total_cases ?? 0
          const completionPercentage = row.original.progress_summary?.completion_percentage ?? 0
          return (
            <div className="flex items-center gap-2 min-w-[120px]">
              <Progress value={Math.min(100, Math.max(0, completionPercentage))} className="h-1.5 flex-1" />
              <span className="text-[11px] text-muted-foreground font-mono tabular-nums whitespace-nowrap">
                {completedCases}/{totalCases}
              </span>
            </div>
          )
        }
      },
      {
        id: "dueDate",
        accessorFn: () => "",
        header: "Due Date",
        cell: () => <span className="text-[11px] font-mono tabular-nums text-muted-foreground">-</span>
      },
      {
        id: "myRole",
        accessorFn: (row) => row.current_user_role ?? "",
        header: "My Role",
        cell: ({ row }) => (
          <span className="text-[11px] px-2 py-0.5 rounded bg-muted/60">
            {formatCurrentUserRole(row.original.current_user_role)}
          </span>
        )
      }
    ],
    []
  )

  return (
    <ProjectListView
      search={search}
      statusFilter={statusFilter}
      showArchived={showArchived}
      selectedTagIds={selectedTagIds}
      tags={tags}
      projects={projects}
      totalProjects={projectsResult?.total ?? 0}
      isLoading={isLoading}
      onSearchChange={setSearch}
      onStatusFilterChange={setStatusFilter}
      onShowArchivedChange={setShowArchived}
      onToggleTag={toggleTag}
      onNewProject={() => navigate(`/projects/new`)}
      onOpenProject={(project) => navigate(`/projects/${project.id}`)}
      columns={columns}
      isInstanceAdmin={isInstanceAdmin}
    />
  )
}

export const ProjectList = ProjectListContainer
